/**
 * Session-scoped permission gate for AMIE.
 *
 * Modules are loaded once per session from PhoneService
 * (`GET /phoneservice/v3/employees/{uac}/modules?jsonFlag=true`) and exposed as
 * a read-only signal. See `features/EB-3709-permission-manager/` for the full
 * design (AD-1 … AD-10).
 *
 * Failure of `loadModules` rethrows after a structured warn log; AuthService
 * uses that signal to flip `accessState` to 'denied-error'.
 */
import { Injectable, Signal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggingService } from '../services/logging.service';
import { safeHttpMessage } from '../error/safe-http-message';

/** Feature areas with read/write gating. Extend the union when a new area lands. */
export type PermissionArea = 'brandManager' | 'callRecording';

/** Raw row shape from PhoneService — only `.name` is consumed (forward-compatible). */
interface RawModule {
  id: number;
  name: string;
}

/** Extends RawModule with the `sources[]` used for department detection (EB-3807). */
interface RawModuleWithSources extends RawModule {
  sources?: Array<{ type: string; refName?: string }>;
}

// AD-8: shared timeout for the modules round-trip — mirrors AuthService.AUTH_TIMEOUT_MS.
const PERMISSIONS_TIMEOUT_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly httpClient = inject(HttpClient);
  private readonly loggingService = inject(LoggingService);

  // AD-3: internal mutator + external read-only signal.
  private readonly _modules = signal<ReadonlySet<string>>(new Set());
  readonly modules: Signal<ReadonlySet<string>> = this._modules.asReadonly();

  // Department membership derived from module sources; consumed by
  // isInDepartment() (EB-3807 Graylog action gates on the Technik department).
  private readonly _departments = signal<ReadonlySet<string>>(new Set());
  readonly departments: Signal<ReadonlySet<string>> = this._departments.asReadonly();

  /**
   * Called once from AuthService.evaluateAccess(). Resolves when modules are
   * loaded (even if the set is empty); rejects on transport failure so the
   * caller can flip accessState to 'denied-error'.
   */
  async loadModules(userAccountId: number): Promise<void> {
    const url = `${environment.phoneServiceUrl}/employees/${userAccountId}/modules?jsonFlag=true`;
    try {
      const response = await firstValueFrom(
        this.httpClient.get<RawModule[]>(url).pipe(timeout(PERMISSIONS_TIMEOUT_MS)),
      );
      this._modules.set(new Set((response ?? []).map((r) => r.name)));
      // Collect DEPARTMENT-typed source names for isInDepartment() (EB-3807).
      const departments = (response ?? [])
        .flatMap((m) => (m as RawModuleWithSources).sources ?? [])
        .filter((s) => s.type === 'DEPARTMENT')
        .map((s) => s.refName)
        .filter((n): n is string => typeof n === 'string' && n.length > 0);
      this._departments.set(new Set(departments));
    } catch (err) {
      // AD-10 / EB-3798: structured warn via LoggingService.
      this.loggingService.warn('permissions.load_failed', {
        uac: userAccountId,
        // safeHttpMessage: never String(err) — an HttpErrorResponse stringifies
        // to "[object Object]" and .message would leak the query string.
        error: safeHttpMessage(err),
      });
      throw err;
    }
  }

  /** Generic — useful for unanticipated module checks. Pure set-membership. */
  hasModule(name: string): boolean {
    return this._modules().has(name);
  }

  /** Department membership check (EB-3807 Graylog action gates on 'Technik'). */
  isInDepartment(name: string): boolean {
    return this._departments().has(name);
  }

  /** Any 'amie.*' module → user is an AMIE user. */
  canAccessAmie(): boolean {
    for (const name of this._modules()) {
      if (name.startsWith('amie.')) return true;
    }
    return false;
  }

  /** Read access to a feature area. `.write` implies `.read`. */
  canRead(area: PermissionArea): boolean {
    return this.hasModule(`amie.${area}.read`) || this.hasModule(`amie.${area}.write`);
  }

  /** Write access to a feature area. `.write` only. */
  canWrite(area: PermissionArea): boolean {
    return this.hasModule(`amie.${area}.write`);
  }

  /** Test-only. Not for production use. */
  resetForTests(): void {
    this._modules.set(new Set());
    this._departments.set(new Set());
  }
}
